import React, { forwardRef, useImperativeHandle } from 'react';
import { useSelectedCompanyHotel } from '@app/site/secured/rateshopper/rates/provider/useSelectedCompanyHotel';
import CompanyHotelMetaradarSettingsInput from '@app/model/company_hotel/input/CompanyHotelMetaradarSettingsInput';
import * as z from 'zod/v4';
import useAppForm from '@app/site/_form';
import { useSaveCompanyMetaradarHotelSettings } from '@app/model/company_hotel/service/CompanyHotelServiceHooks';
import Region, { metaradarEnabledRegions } from '@app/model/market_state/Region';
import DataProvider from '@app/model/_common/DataProvider';
import Utilities from '@util/Utilities';
import useToaster from '@app/component/overlays/toaster/ToasterHook';
import { useQueryClient } from '@tanstack/react-query';
import { MetaradarQueryKeys } from '@app/model/metaradar/service/MetaradarQueryKeys';
import Drawer from '@app/component/overlays/drawer/Drawer';
import useDrawer from '@app/component/overlays/drawer/DrawerRef';

export interface MetaradarSettingsFragmentRef {
	open: () => void;
	close: () => void;
}

const MetaradarSettingsFragment = React.memo(forwardRef<MetaradarSettingsFragmentRef>((props, ref) => {

	const { companyHotel } = useSelectedCompanyHotel();
	const toaster = useToaster();
	const drawer = useDrawer();
	const queryClient = useQueryClient();

	useImperativeHandle(ref, () => ({
		open: () => drawer.open(),
		close: () => drawer.close(),
	}));

	const saveCompanyMetaradarHotelSettings = useSaveCompanyMetaradarHotelSettings({
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: MetaradarQueryKeys.companyHotel(companyHotel.id) })
				.then(() => {
					drawer.close();
					toaster.success('Metaradar', 'Metaradar settings saved successfully!')
				});
		},
	});

	const defaultMetaradarSettingsValues: CompanyHotelMetaradarSettingsInput = {
		providers: !!companyHotel.metaradarSettings ? companyHotel.metaradarSettings.providers : [],
		regions: !!companyHotel.metaradarSettings ? companyHotel.metaradarSettings.regions : [],
		nights: !!companyHotel.metaradarSettings ? companyHotel.metaradarSettings.nights : 1,
		threshold: companyHotel.metaradarSettings?.threshold,
	};

	const metaradarSettingsValidation = z.object({
		providers: z.array(z.enum(Object.values(DataProvider)))
			.min(1, 'Add at least one provider.'),
		regions: z.array(z.enum(Object.values(Region)))
			.min(1, 'Add at least one region.'),
		nights: z.number().positive('Please add a nights value'),
		threshold: z.preprocess(
			(value) => {
				if (value === null || value === undefined) return undefined;
				if (typeof value === 'string' && value.trim() === '') return undefined;
				return value;
			},
			z.coerce.number()
				.positive('Please add a positive value')
				.optional(),
		),
	});

	const form = useAppForm({
		defaultValues: defaultMetaradarSettingsValues,
		validators: {
			// @ts-ignore
			onChange: metaradarSettingsValidation,
		},
		onSubmit: async ({ value }) => {
			const parsedInput = metaradarSettingsValidation.parse(value) as CompanyHotelMetaradarSettingsInput;
			return saveCompanyMetaradarHotelSettings.mutateAsync({
				companyHotelId: companyHotel.id,
				input: parsedInput,
			}).catch(() => {
			});
		},
	});
	return <form.AppForm>
		<form onSubmit={
			(e) => {
				e.preventDefault();
				e.stopPropagation();
			}
		}>
			<Drawer ref={drawer.ref} title="Configure Metaradar" actions={
				<>
					<form.ResetButton state="text">
						Reset
					</form.ResetButton>
					<form.SubmitButton state="primary">
						Save
					</form.SubmitButton>
				</>
			}>
				<div className="mt-2 px-6 py-5">
					<div className="text-sm text-pretty">
						<p className="tracking-tight text-gray-500">Configuration settings regarding your metaradar execution.</p>
					</div>
					<div className="mt-3">

						<div className="rounded-md bg-gray-50 ring-1 ring-black ring-opacity-10">
							<div className="px-4 py-5 space-y-6">
								<form.AppField name="regions"
															 children={(field) => <field.MultiSelectField label="Countries"
																																						labelHint="(Up to 6 selections)"
																																						placeholder="Select countries"
																																						extraClassName="w-full"
																																						maxSelections={6}
																																						options={Object.values(metaradarEnabledRegions)
																																							.map(v => ({
																																								id: v,
																																								label: Utilities.snakeCaseToWords(v),
																																								value: v,
																																							}))
																																							.sort((a, b) => a.label.localeCompare(b.label))} />} />
								<form.AppField name="providers"
															 children={(field) => <field.MultiSelectField label="Channels"
																																						placeholder="Select channels"
																																						extraClassName="w-full"
																																						maxSelections={6}
																																						options={[
																																							{
																																								id: DataProvider.AGODA,
																																								label: 'Agoda',
																																								value: DataProvider.AGODA,
																																							},
																																							{
																																								id: DataProvider.TRIP_ADVISOR,
																																								label: 'Trip Advisor',
																																								value: DataProvider.TRIP_ADVISOR,
																																							},
																																							{
																																								id: DataProvider.BOOKING,
																																								label: 'Booking',
																																								value: DataProvider.BOOKING,
																																							},
																																							{
																																								id: DataProvider.HOTELS_COM,
																																								label: 'Hotels.com',
																																								value: DataProvider.HOTELS_COM,
																																							},
																																							{
																																								id: DataProvider.EXPEDIA,
																																								label: 'Expedia.com',
																																								value: DataProvider.EXPEDIA,
																																							},
																																							{
																																								id: DataProvider.TRIP_COM,
																																								label: 'Trip.com',
																																								value: DataProvider.TRIP_COM,
																																							},
																																							{
																																								id: DataProvider.TRAVELSUPERMARKET,
																																								label: 'travelsupermarket.com',
																																								value: DataProvider.TRAVELSUPERMARKET,
																																							},
																																						]} />
															 } />
								<form.AppField name="nights">
									{(field)=><field.SelectField label="Nights" extraClassName="w-full" options={[
										{id: 'nights1', value: 1, label: '1 Nights'},
										{id: 'nights2', value: 2, label: '2 Nights'},
										{id: 'nights3', value: 3, label: '3 Nights'},
										{id: 'nights4', value: 4, label: '4 Nights'},
										{id: 'nights5', value: 5, label: '5 Nights'},
										{id: 'nights6', value: 6, label: '6 Nights'},
										{id: 'nights7', value: 7, label: '7 Nights'}
									]} />}
								</form.AppField>

								<form.AppField name="threshold"
															 children={(field) => <field.TextField label="Threshold" placeholder="Insert threshold"
																																		 labelHint="(Optional. If you choose to fill it, enter an amount in EUR)"
																																		 extraClassName="w-full" />} />
							</div>
						</div>
					</div>
				</div>
			</Drawer>
		</form>
	</form.AppForm>;

}));

export default MetaradarSettingsFragment;
